import * as Sentry from '@sentry/react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  type GetWashboardDevicesParams,
  type GetWashboardDevicesResponse,
  type WashboardDevice,
  getWashboardDevices,
} from 'api/admin/washboard/washboard';

type UseWashboardDevicesParams = Pick<
  GetWashboardDevicesParams,
  'page' | 'limit' | 'businessId' | 'storeId' | 'deviceType' | 'searchTerm' | 'sortBy' | 'sortOrder'
>;

type UseWashboardDevicesResult = {
  devices: WashboardDevice[];
  total: number;
  hasMore: boolean;
};

const errorHandler = (error: unknown) => {
  Sentry.captureException(error);
};

export const useWashboardDevices = ({
  page,
  limit,
  businessId,
  storeId,
  deviceType,
  searchTerm,
  sortBy,
  sortOrder,
}: UseWashboardDevicesParams): UseQueryResult<UseWashboardDevicesResult, unknown> => {
  return useQuery({
    queryKey: [
      'washboardDevices',
      page,
      limit,
      businessId ?? null,
      storeId ?? null,
      deviceType ?? null,
      searchTerm ?? null,
      sortBy ?? null,
      sortOrder ?? null,
    ],
    queryFn: async () => {
      const { data } = (await getWashboardDevices({
        page,
        limit,
        businessId,
        storeId,
        deviceType,
        searchTerm,
        sortBy,
        sortOrder,
      })) as AxiosResponse<GetWashboardDevicesResponse>;

      return {
        devices: data.devices,
        total: data.total,
        hasMore: data.hasMore,
      };
    },
    onError: errorHandler,
    retry: 1,
    keepPreviousData: true,
  });
};
